import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api/client";
import { getYardState, type YardState } from "../../staff/yard-state";

export type AdminYard = {
  id: number;
  name: string;
  moaCompanyId: number | null;
  moaCompanyName: string | null;
  /** Derived yard state (préparation, en service, clôturé…). */
  state: YardState;
  /** Whether the yard is still in its preparation period. */
  inPreparation: boolean;
};

export type UseYardResult = {
  yard: AdminYard | null;
  loading: boolean;
  refetch: () => Promise<void>;
};

/**
 * Fetches a single yard by id. Shared by the yard settings menu (screen 903)
 * and its sub-pages.
 */
export function useYard(yardId: number): UseYardResult {
  const [yard, setYard] = useState<AdminYard | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchYard = useCallback(async () => {
    setLoading(true);
    const { data, error, response } = await apiClient.GET(
      "/api/admin/yards/{id}",
      { params: { path: { id: yardId } } },
    );
    // Only a 404 means the yard is gone ("Chantier introuvable"); on any
    // other failure keep what was already loaded.
    if (error && response.status !== 404) {
      setLoading(false);
      return;
    }

    if (data) {
      const found = data.data.yard;
      const state = getYardState(found);
      setYard({
        id: found.id,
        name: found.name,
        moaCompanyId: found.moaCompanyId,
        moaCompanyName: found.moaCompany?.name ?? null,
        state,
        inPreparation: state === "preparation",
      });
    } else {
      setYard(null);
    }
    setLoading(false);
  }, [yardId]);

  useEffect(() => {
    fetchYard();
  }, [fetchYard]);

  return { yard, loading, refetch: fetchYard };
}
